import React from 'react';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import checkIsTicketGroupOnSale from '@dashboard/services/event/checkIsTicketGroupOnSale';
import checkIsTicketGroupScheduled from '@repo/checkout/lib/checkIsTicketGroupScheduled';
import checkIsEventSoldOut from '@dashboard/services/event/checkIsEventSoldOut';
import checkIsTicketGroupSoldOut from '@repo/checkout/lib/checkIsTicketGroupSoldOut';
import checkIsTicketGroupNotOnSale from '@repo/checkout/lib/checkIsTicketGroupNotOnSale';

export default function TicketGroupEventOverviewBadges({
  page,
  ticketGroup,
  allTicketGroups = [],
  showOnlyOneBadge = false,
}) {
  let primaryBadge = null;
  let hiddenBadge = null;
  let disabledBadge = null;

  const isOnSale = checkIsTicketGroupOnSale({ page, ticketGroup, allTicketGroups });
  const isCanceled = page.eventDetails?.status === 'canceled';

  if (isCanceled) {
    return (
      <div className="flex gap-x-2 flex-wrap">
        <div>
          <Tooltip label="This event is canceled.">
            <StatusBadge label="Canceled" status="canceled"></StatusBadge>
          </Tooltip>
        </div>
      </div>
    );
  }

  if (ticketGroup.status === 'archived') {
    return (
      <div className="flex gap-x-2 flex-wrap">
        <div>
          <Tooltip label="Archived ticket groups will not be shown to customers.">
            <StatusBadge status="archived" label="Archived" />
          </Tooltip>
        </div>
      </div>
    );
  }

  if (ticketGroup.status === 'disabled') {
    return (
      <div className="flex gap-x-2 flex-wrap">
        <div>
          <Tooltip label="Disabled ticket groups will not be shown to customers.">
            <StatusBadge status="disabled" label="Disabled" />
          </Tooltip>
        </div>
      </div>
    );
  }

  const isSoldOut = checkIsTicketGroupSoldOut(ticketGroup, { excludeTicketTypes: true });
  const isUnavailable = ticketGroup.availabilityStatus === 'unavailable';
  const { isScheduled, hasInvalidReference } = checkIsTicketGroupScheduled({
    ticketGroup,
    allTicketGroups,
  });

  const isNotOnSale = checkIsTicketGroupNotOnSale({
    ticketGroup,
    isCanceled,
    isOnSale,
    isScheduled,
    hasInvalidReference,
  });

  if (
    checkIsEventSoldOut(page) ||
    checkIsTicketGroupSoldOut(ticketGroup, { excludeTicketTypes: true })
  ) {
    primaryBadge = {
      content: <StatusBadge status="sold_out" label="Sold out" />,
      tooltip: 'This ticket group no longer has availability.',
    };
  } else if (ticketGroup.availabilityStatus === 'unavailable') {
    primaryBadge = {
      content: <StatusBadge status="unavailable" label="Unavailable" />,
      tooltip: 'This ticket group is shown as unavailable to customers.',
    };
  } else if (ticketGroup.availabilityStatus === 'available' && !isOnSale) {
    const { isScheduled } = checkIsTicketGroupScheduled({ ticketGroup, allTicketGroups });

    if (isScheduled) {
      primaryBadge = {
        content: <StatusBadge status="scheduled" label="On sale soon" />,
        tooltip: 'This ticket group is scheduled to go on sale.',
      };
    }
  } else if (ticketGroup.availabilityStatus === 'available' && isOnSale) {
    primaryBadge = {
      content: <StatusBadge status="on_sale" label="On sale" />,
      tooltip: 'This ticket group is currently on sale.',
    };
  }

  const showHiddenTicketBadge =
    ticketGroup.hidden ||
    (ticketGroup.hideWhenNotOnSale && isNotOnSale) ||
    (ticketGroup.hideWhenScheduled && isScheduled) ||
    (ticketGroup.hideWhenUnavailable && isUnavailable) ||
    (ticketGroup.hideWhenSoldOut && isSoldOut);

  // Hidden badge (can show alongside any primary badge)
  if (showHiddenTicketBadge) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket group is hidden.',
    };
  }

  if (!primaryBadge && ticketGroup.status === 'enabled') {
    primaryBadge = {
      content: <StatusBadge status="sale_ended" label="Not on sale" />,
      tooltip: 'This ticket group is currently not on sale.',
    };
  }

  const badges = [];
  if (showOnlyOneBadge) {
    if (disabledBadge) {
      badges.push({ key: 'disabled', ...disabledBadge });
    } else if (primaryBadge) {
      badges.push({ key: 'primary', ...primaryBadge });
    } else if (!disabledBadge && hiddenBadge) {
      badges.push({ key: 'hidden', ...hiddenBadge });
    }
  } else {
    if (primaryBadge) badges.push({ key: 'primary', ...primaryBadge });
    if (disabledBadge) badges.push({ key: 'disabled', ...disabledBadge });
    if (!disabledBadge && hiddenBadge) badges.push({ key: 'hidden', ...hiddenBadge });
  }

  if (badges.length === 0) {
    return null;
  }

  return (
    <div className="flex gap-x-2 flex-wrap">
      {badges.map(badge => (
        <div key={badge.key}>
          {badge.tooltip ? <Tooltip label={badge.tooltip}>{badge.content}</Tooltip> : badge.content}
        </div>
      ))}
    </div>
  );
}
